import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import JsonLd, { SITE_URL } from "@/components/feature/JsonLd";

const MAIN_SITE = "https://kaspreadsheet.com";

const SAFETY_TIPS = [
  {
    icon: "ri-link-unlink-m",
    title: "Verify Every Link",
    desc: "Always double-click links before visiting. Phishing sites often mimic real platforms with slightly different URLs. Bookmark the official USFans website and only use that link.",
  },
  {
    icon: "ri-user-search-line",
    title: "Check Seller Reputation",
    desc: "Look for seller reviews, transaction history and community feedback. Avoid sellers with no track record or negative patterns across multiple forums.",
  },
  {
    icon: "ri-price-tag-3-line",
    title: "Compare Prices",
    desc: "If a price seems too good to be true, it usually is. Cross-reference the same product across multiple sellers before committing.",
  },
  {
    icon: "ri-truck-line",
    title: "Confirm Shipping Details",
    desc: "Verify shipping costs, estimated delivery windows and tracking availability before placing any order. Hidden shipping fees are a common surprise.",
  },
  {
    icon: "ri-shield-keyhole-line",
    title: "Protect Payment Info",
    desc: "Use secure payment methods with buyer protection. Never share bank details, passwords or one-time codes through chat or email.",
  },
  {
    icon: "ri-file-list-3-line",
    title: "Keep Records",
    desc: "Save screenshots of listings, conversations and payment confirmations. Good records help resolve disputes quickly if something goes wrong.",
  },
];

const RED_FLAGS = [
  "Requests for payment outside the official platform",
  "Sellers refusing to provide tracking numbers",
  "Prices dramatically lower than market average",
  "Websites with no SSL certificate (missing padlock icon)",
  "Pressure to complete a purchase immediately",
  "Communication only through unofficial channels",
  "Spreadsheet links shared by unknown sources",
  "No return policy or refund guarantee listed",
];

const CHECKLIST = [
  { label: "Seller has visible reviews or references", done: false },
  { label: "Price matches similar listings elsewhere", done: false },
  { label: "Shipping cost and timeline are clearly stated", done: false },
  { label: "Website uses HTTPS (secure connection)", done: false },
  { label: "Payment method offers buyer protection", done: false },
  { label: "You saved screenshots of the listing", done: false },
  { label: "Return or refund policy is documented", done: false },
  { label: "You verified the link is the official site", done: false },
];

const SAFETY_FAQS = [
  {
    q: "Is the USFans Spreadsheet safe to use?",
    a: "The spreadsheet itself is a neutral document. Safety depends on how you use it: always verify sellers independently, confirm links are legitimate and double-check prices before buying.",
  },
  {
    q: "How do I spot a fake USFans website?",
    a: "Check the domain carefully. Official sites will have consistent branding. Look for HTTPS, correct spelling and an about or contact page. If anything feels off, do not proceed.",
  },
  {
    q: "What should I do if a seller asks for direct payment?",
    a: "Decline. Legitimate sellers accept payment through the official platform or other secure methods with buyer protection. Direct bank transfers, gift cards or crypto are red flags.",
  },
  {
    q: "Can a spreadsheet guarantee product quality?",
    a: "No. A spreadsheet organizes information but cannot verify authenticity. Always research individual products and sellers beyond what any spreadsheet shows.",
  },
  {
    q: "What payment methods are safest?",
    a: "Credit cards and well-known payment services with buyer protection are the safest options. They offer dispute resolution if the product never arrives or is not as described.",
  },
];

export default function Safety() {
  const { lang } = useParams();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <article className="w-full">
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "USFans Safety Center",
          url: `${SITE_URL}/en/safety/`,
          inLanguage: "en",
          description:
            "Learn how to verify sellers, spot red flags and protect yourself when using the USFans Spreadsheet and any related marketplace.",
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/en/` },
              {
                "@type": "ListItem",
                position: 2,
                name: "Safety Center",
                item: `${SITE_URL}/en/safety/`,
              },
            ],
          },
        }}
      />
      {/* Hero */}
      <div className="relative bg-background-100 border-b border-background-200">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-14 md:py-20 text-center">
          <nav className="text-xs text-foreground-500 mb-5 text-left">
            <Link to={`/${lang}`} className="hover:text-foreground-900 transition">
              Home
            </Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-700">Safety Center</span>
          </nav>

          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-100 text-accent-900 mx-auto mb-5">
            <i className="ri-shield-star-line text-3xl"></i>
          </span>
          <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-foreground-950 leading-tight mb-4">
            USFans Safety Center
          </h1>
          <p className="text-foreground-600 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            Research smarter and stay safe. Learn how to verify sellers, spot red flags and protect yourself when using the USFans Spreadsheet and any related marketplace.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="space-y-14 text-foreground-800 leading-relaxed">
          {/* Intro */}
          <section>
            <p className="text-base md:text-lg">
              The USFans Spreadsheet is a powerful research tool — but like any resource that connects you with sellers and products, you need to stay alert. This safety guide covers everything you should check before making a purchase, from verifying links to protecting your payment details.
            </p>
          </section>

          {/* Safety tips */}
          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              Core Safety Tips
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {SAFETY_TIPS.map((tip) => (
                <div
                  key={tip.title}
                  className="rounded-lg border border-background-200 bg-background-50 p-5 hover:border-accent-300 transition"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-900 mb-3">
                    <i className={`${tip.icon} text-xl`}></i>
                  </span>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                    {tip.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{tip.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Red flags */}
          <section className="rounded-lg bg-accent-50 border border-accent-200 p-6 md:p-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5 flex items-center gap-3">
              <i className="ri-alarm-warning-line text-accent-600"></i>
              Red Flags to Watch For
            </h2>
            <ul className="space-y-3">
              {RED_FLAGS.map((flag) => (
                <li key={flag} className="flex items-start gap-3">
                  <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-200 text-accent-800 shrink-0 mt-0.5">
                    <i className="ri-close-line text-xs"></i>
                  </span>
                  <span className="text-sm md:text-base text-foreground-800">{flag}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Checklist */}
          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              Pre-Purchase Safety Checklist
            </h2>
            <p className="text-foreground-600 mb-5">
              Before you buy through any seller listed in a USFans Spreadsheet, run through this quick checklist:
            </p>
            <div className="rounded-lg border border-background-200 overflow-hidden">
              {CHECKLIST.map((item, i) => (
                <div
                  key={item.label}
                  className={`flex items-start gap-3 px-4 md:px-5 py-3.5 ${
                    i % 2 === 0 ? "bg-background-50" : "bg-background-100/50"
                  }`}
                >
                  <span className="w-5 h-5 flex items-center justify-center rounded border border-background-300 bg-background-50 shrink-0 mt-0.5">
                    <i className="ri-check-line text-xs text-foreground-400"></i>
                  </span>
                  <span className="text-sm md:text-base text-foreground-800">{item.label}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Safety FAQs */}
          <section>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
              Safety FAQs
            </h2>
            <div className="space-y-5">
              {SAFETY_FAQS.map((f) => (
                <div key={f.q}>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1.5">
                    {f.q}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Internal link */}
          <section className="rounded-lg bg-accent-50 border border-accent-200 p-6">
            <p className="text-foreground-700">
              Want to learn more about USFans research? Check out our{" "}
              <Link
                to={`/${lang}/article/usfans-spreadsheet-guide`}
                className="text-primary-700 font-semibold hover:underline"
              >
                USFans Spreadsheet Guide
              </Link>{" "}
              or return to the{" "}
              <Link to={`/${lang}`} className="text-primary-700 font-semibold hover:underline">
                home page
              </Link>{" "}
              for more resources.
            </p>
          </section>

          {/* Final CTA */}
          <section className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8 text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
              Ready to Browse the USFans Spreadsheet?
            </h2>
            <p className="text-foreground-600 mb-6">
              Explore the full product catalog on the main USFans Spreadsheet website — now that you know how to stay safe.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={MAIN_SITE}
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto h-11 px-6 inline-flex items-center justify-center rounded-md bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition whitespace-nowrap"
              >
                Visit Main Website
                <i className="ri-arrow-right-up-line ml-2"></i>
              </a>
              <Link
                to={`/${lang}`}
                className="w-full sm:w-auto h-11 px-6 inline-flex items-center justify-center rounded-md border border-background-300 text-foreground-900 font-semibold hover:bg-background-200 transition whitespace-nowrap"
              >
                Back to Home
              </Link>
            </div>
          </section>
        </div>
      </div>
    </article>
  );
}